import { get, patch, post, remove } from "../http-client/client";
import { ApiResponse } from "../http-client/Response";

export interface OrganizationAdminSummary {
  id: string;
  name: string;
  isTrial: boolean;
  trialEndsAt: string | null;
  userCount: number;
  tokenLimit: number | null;
  tokensConsumed: number;
  plan: OrganizationPlan;
  planEndsAt: string | null;
  seatLimit: number | null;
  costCeilingSek: number | null;
  costMultiplier: number;
  isWorkspace: boolean;
  slug: string | null;
  displayName: string | null;
}

export type OrganizationPlan = "trial" | "active" | "paused" | "cancelled";

export const updateOrganizationPlan = (
  organizationId: string,
  changes: {
    plan?: OrganizationPlan;
    planEndsAt?: string | null;
    seatLimit?: number | null;
    tokenLimit?: number | null;
    costCeilingSek?: number | null;
    costMultiplier?: number;
  },
): Promise<ApiResponse<OrganizationAdminSummary>> =>
  patch(`/admin/organizations/${organizationId}/plan`, changes, null);

export const listOrganizations = (): Promise<
  ApiResponse<OrganizationAdminSummary[]>
> => {
  return get(`/admin/organizations`, null);
};

export const deleteOrganization = (
  organizationId: string,
): Promise<ApiResponse<void>> => {
  return remove(`/admin/organizations/${organizationId}`, null);
};

export interface OrganizationAdminUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  jobTitle: string | null;
  /** ACTIVE, or UNCONFIRMED (invited, has not set a password yet) */
  status: string;
  createdAt: string;
  lastActiveAt: string | null;
  chatCount: number;
  costUsd30d: number;
  /** costUsd30d in SEK at the shared rate (raw model cost) */
  costSek30d: number;
  /** Left out of member lists shown to other members */
  hiddenFromMembers: boolean;
  /** 'admin' manages the organization's members */
  role: string;
  organizationId: string;
  company: string | null;
  teamSize: string | null;
  legalHoursPerWeek: number | null;
  meetingAssistantEnabled: boolean;
}

/** Every user of every organization */
export const listAllUsers = (): Promise<ApiResponse<OrganizationAdminUser[]>> =>
  get(`/admin/organizations/users`, null);

export interface OrganizationAdminInvitation {
  id: string;
  email: string | null;
  invitedBy: string | null;
  createdAt: string;
  expiresAt: string;
  expired: boolean;
}

export interface OrganizationAdminDetail {
  organization: OrganizationAdminSummary;
  users: OrganizationAdminUser[];
  pendingInvitations: OrganizationAdminInvitation[];
}

export const getOrganizationDetail = (
  organizationId: string,
): Promise<ApiResponse<OrganizationAdminDetail>> => {
  return get(`/admin/organizations/${organizationId}`, null);
};

export const setUserHiddenFromMembers = (
  organizationId: string,
  userId: string,
  hiddenFromMembers: boolean,
): Promise<ApiResponse<void>> => {
  return patch(
    `/admin/organizations/${organizationId}/users/${userId}`,
    { hiddenFromMembers },
    null,
  );
};

export const revokeInvitation = (invitationId: string): Promise<ApiResponse<void>> =>
  remove(`/admin/invitations/${invitationId}`, null);

export const resendInvitation = (invitationId: string): Promise<ApiResponse<void>> =>
  post(`/admin/invitations/${invitationId}/resend`, {}, null);

export const setUserRole = (
  organizationId: string,
  userId: string,
  role: "member" | "admin",
): Promise<ApiResponse<void>> =>
  patch(`/admin/organizations/${organizationId}/users/${userId}`, { role }, null);
